import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./index.module.css";
import Logo from "../../../Utils/icons/eden_Care_Logo.png";
import newLogo from "../../../Utils/icons/newLogo.png";
import DashboardIcon from "../../../Utils/icons/pages/Dashboard.png";
import ProspectsIcon from "../../../Utils/icons/pages/Prospects.png";
import QuotesIcon from "../../../Utils/icons/pages/Quotes.png";
import ClientsIcon from "../../../Utils/icons/pages/Clients.png";
import CommissionsIcon from "../../../Utils/icons/pages/Commissions.png";
import ucare from "../../../Utils/icons/enhancedCare.png";
import salesPerformance from "../../../Utils/icons/createQuoteHealthInsuranceIcon.png";
import audit from "../../../Utils/icons/clinetsCompanyPhoto.png";
import addUser from "../../../Utils/icons/addUser.svg";
import LogoutIcon from "../../../Utils/icons/pages/Logout.png";
import debtManagement from "../../../Utils/icons/broadVeiw.png";

const navGroups = [
  {
    label: "Main",
    items: [
      { name: "Dashboard", path: "/", icon: DashboardIcon },
      { name: "Prospects", path: "/prospects", icon: ProspectsIcon },
      { name: "Quotes", path: "/quotes", icon: QuotesIcon },
      { name: "Policies", path: "/policies", icon: ClientsIcon },
      { name: "Commissions", path: "/commissions", icon: CommissionsIcon },
      {
        name: "Debt Management",
        path: "/debt-management",
        icon: debtManagement,
      },
    ],
  },
  {
    label: "Tools",
    items: [
      { name: "U-Care", path: "/ucare", icon: ucare, external: true },
      {
        name: "Sales Performance",
        path: "/sales-performance",
        icon: salesPerformance,
      },
    ],
  },
  {
    label: "Settings & Logs",
    items: [
      { name: "Audit Log", path: "/audit-log", icon: audit },
      { name: "Users", path: "/users", icon: addUser },
    ],
  },
];

const NavItem = ({ item, open, index }) => {
  const isExternal = item.external;
  const content = (
    <motion.span
      className={styles.navItemInner}
      style={{ justifyContent: open ? "flex-start" : "center" }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.6,
        delay: index * 0.08,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      <motion.img
        className={styles.sidebarIcon}
        src={item.icon}
        alt=""
        aria-hidden
        whileHover={{ scale: 1.15 }}
        transition={{ type: "spring", stiffness: 300, damping: 15 }}
      />
      <AnimatePresence>
        {open && (
          <motion.span
            className={styles.itemLabel}
            initial={{ opacity: 0, x: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -16, filter: "blur(6px)" }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {item.name}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.span>
  );

  if (isExternal && item.path === "/ucare") {
    return (
      <a
        href="https://ucare-tau.vercel.app/"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.linkItem}
        aria-label={`Open ${item.name} in new tab`}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      className={({ isActive }) =>
        isActive
          ? `${styles.linkItem} ${styles.linkItemActive}`
          : styles.linkItem
      }
    >
      {content}
    </NavLink>
  );
};

const SideBar = () => {
  const [open, setOpen] = useState(true);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <motion.aside
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className={open ? styles.sideBarOpen : styles.sideBar}
      initial={false}
      animate={{ width: open ? 260 : 80 }}
      transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Link to="/" className={styles.logoLink} aria-label="Go to Dashboard">
        <motion.img
          src={open ? Logo : newLogo}
          className={styles.logo}
          alt="Eden Care"
          key={open ? "full" : "mini"}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </Link>

      <nav
        className={styles.nav}
        aria-label="Main navigation"
        style={{ marginTop: "40px" }}
      >
        <div className={styles.navScroll}>
          {navGroups.map((group) => (
            <div key={group.label} className={styles.navGroup}>
              <AnimatePresence>
                {open && (
                  <motion.span
                    className={styles.groupLabel}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    {group.label}
                  </motion.span>
                )}
              </AnimatePresence>
              <div className={styles.groupItems}>
                {group.items.map((item, i) => (
                  <NavItem key={item.name} item={item} open={open} index={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      <div className={styles.footer}>
        <motion.button
          type="button"
          className={styles.logoutBtn}
          onClick={handleLogout}
          aria-label="Logout"
          style={{ justifyContent: open ? "flex-start" : "center" }}
          whileHover={{ x: 4 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <motion.img
            className={styles.sidebarIcon}
            src={LogoutIcon}
            alt=""
            aria-hidden
            whileHover={{ rotate: -10 }}
            transition={{ type: "spring", stiffness: 200 }}
          />
          <AnimatePresence>
            {open && (
              <motion.span
                className={styles.itemLabel}
                initial={{ opacity: 0, x: -16, filter: "blur(6px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, x: -16, filter: "blur(6px)" }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Logout
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.aside>
  );
};

export default SideBar;
